// Logique de session EtikID — PURE et testable : le stockage est injecté, donc
// aucune dépendance à sessionStorage ni au DOM. Le flux navigateur (redirections)
// vit dans etikid.js et s'appuie sur ce module.
//
// Trois défauts de l'original sont traités ici. Ils ont un point commun : ils ne
// dégradent pas l'app, ils ENFERMENT l'utilisateur dehors, sans message.

export const SESSION_KEY = "bureau-etikid-session";
export const VERIFIER_KEY = "etikid_verifier";
export const STATE_KEY = "etikid_state";
export const BOUNCE_KEY = "etikid_bounce_count";

/** Le token EtikID vit ~1 h ; on relance la connexion un peu avant. */
export const TOKEN_MAX_AGE_MS = 55 * 60 * 1000;

/**
 * Plafond de rebonds vers EtikID, aligné sur apps/rh-v2 (MAX_BOUNCE = 2).
 *
 * CORRIGÉ (B2) — sans ce plafond, la boucle est INFINIE ET SILENCIEUSE : l'EF
 * refuse le token (app non accordée à cette personne, ou token sans tenant_id),
 * le front repart en connexion, revient avec un token tout aussi refusé, et
 * recommence. L'utilisateur voit son écran clignoter sans jamais comprendre.
 * C'est le scénario probable pour la première personne à qui on ouvrira l'app
 * sans lui avoir accordé « bureau » dans EtikID.
 */
export const MAX_BOUNCE = 2;

/** Store en mémoire — utilisé par les tests, et repli si sessionStorage est bloqué. */
export function memoryStore(initial = {}) {
  const data = { ...initial };
  return {
    getItem: (k) => (k in data ? data[k] : null),
    setItem: (k, v) => {
      data[k] = String(v);
    },
    removeItem: (k) => {
      delete data[k];
    },
  };
}

/**
 * Store du navigateur, avec repli mémoire.
 * Safari en navigation privée peut faire lever sessionStorage : sans ce repli,
 * l'app ne démarre pas du tout.
 */
export function browserStore() {
  try {
    const sonde = "__bureau_probe__";
    sessionStorage.setItem(sonde, "1");
    sessionStorage.removeItem(sonde);
    return sessionStorage;
  } catch {
    return memoryStore();
  }
}

/** Lit la session stockée, ou null si absente/illisible. */
export function lireSession(store) {
  try {
    return JSON.parse(store.getItem(SESSION_KEY) || "null");
  } catch {
    return null;
  }
}

/** Token encore utilisable ? (présent ET pas trop vieux) */
export function tokenValide(session, maintenant = Date.now()) {
  if (!session || !session.access_token || !session.authedAt) return false;
  return maintenant - session.authedAt < TOKEN_MAX_AGE_MS;
}

/** Token courant, ou chaîne vide. */
export function tokenCourant(store, maintenant = Date.now()) {
  const s = lireSession(store);
  return tokenValide(s, maintenant) ? s.access_token : "";
}

export function ecrireSession(store, accessToken, maintenant = Date.now()) {
  try {
    store.setItem(SESSION_KEY, JSON.stringify({ access_token: accessToken, authedAt: maintenant }));
  } catch {
    /* stockage indisponible : la session ne survivra pas au rechargement, l'app fonctionne quand même */
  }
}

export function effacerSession(store) {
  try {
    store.removeItem(SESSION_KEY);
  } catch {
    /* rien à faire */
  }
}

// ── Circuit-breaker anti-boucle ───────────────────────────────────────────────

export function peutRebondir(store) {
  return Number(store.getItem(BOUNCE_KEY) || "0") < MAX_BOUNCE;
}

export function marquerRebond(store) {
  store.setItem(BOUNCE_KEY, String(Number(store.getItem(BOUNCE_KEY) || "0") + 1));
}

/** À appeler dès qu'une connexion RÉUSSIT : la série de rebonds est rompue. */
export function reinitialiserRebonds(store) {
  store.removeItem(BOUNCE_KEY);
}

/**
 * Vérifie le `state` anti-CSRF du retour EtikID, puis le CONSOMME.
 *
 * Le consommer est ce qui empêche un `state` de resservir. L'original le laissait
 * en place, ce qui, combiné à deux redirections concurrentes (B3), produisait un
 * « Session invalide — réessayez » dont on ne sortait qu'en vidant le stockage.
 */
export function verifierEtConsommerState(store, stateRecu) {
  const attendu = store.getItem(STATE_KEY);
  store.removeItem(STATE_KEY);
  return Boolean(attendu) && stateRecu === attendu;
}

/**
 * Décode les claims du token SANS vérification cryptographique.
 * Sert UNIQUEMENT à montrer ou cacher des éléments d'interface. L'autorisation
 * réelle est faite côté serveur, sur le token validé — jamais sur ceci.
 */
export function decoderClaims(jwt) {
  try {
    const payload = JSON.parse(atob(jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    const m = payload.app_metadata ?? {};
    return {
      tenantId: m.tenant_id ?? null,
      tenantNom: m.tenant_nom ?? null,
      isAdmin: Boolean(m.is_admin),
    };
  } catch {
    return { tenantId: null, tenantNom: null, isAdmin: false };
  }
}
